import React, { useRef, useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaPaperPlane } from "react-icons/fa";
import emailjs from "@emailjs/browser";
import ReCAPTCHA from "react-google-recaptcha";
import Notification from "../components/Notifications/Notification";

const ContactForm: React.FC = () => {
  const form = useRef<HTMLFormElement>(null);
  const { t } = useTranslation();
  const location = useLocation();

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<
    { id: number; type: "success" | "error" | "info"; message: string }[]
  >([]);
  const [prefilledMessage, setPrefilledMessage] = useState("");

  // ✅ Récupération du message pré-rempli si présent dans l'URL
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const message = queryParams.get("message") || "";
    setPrefilledMessage(message);
  }, [location.search]);

  // ✅ Ajout d'une notification
  const addNotification = useCallback(
    (type: "success" | "error" | "info", key: string) => {
      const id = Date.now();
      setNotifications((prev) => [...prev, { id, type, message: t(key) }]);
      setTimeout(() => removeNotification(id), 5000);
    },
    [t]
  );

  // ✅ Suppression d'une notification
  const removeNotification = useCallback((id: number) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
  }, []);

  // ✅ Validation du formulaire
  const validateForm = useCallback(() => {
    if (!form.current) return false;
    let isValid = true;

    const inputs = form.current.querySelectorAll("input, textarea");
    inputs.forEach((input) => {
      if (!(input as HTMLInputElement | HTMLTextAreaElement).value.trim()) {
        isValid = false;
      }
    });

    return isValid;
  }, []);

  // ✅ Envoi de l'email avec EmailJS
  const sendEmail = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      addNotification("error", "contactForm.missingFields");
      return;
    }

    if (!captchaToken) {
      addNotification("error", "contactForm.captchaError");
      return;
    }

    if (!form.current) {
      addNotification("error", "contactForm.emailError");
      return;
    }

    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID as string,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID as string,
        form.current,
        process.env.REACT_APP_EMAILJS_USER_ID as string
      );

      addNotification("success", "contactForm.emailSuccess");
      form.current.reset();
      setCaptchaToken(null);
    } catch (error) {
      addNotification("error", "contactForm.emailError");
    }
  };

  return (
    <>
      {/* ✅ Zone des notifications */}
      <div className="notification-container">
        <AnimatePresence>
          {notifications.map((notif) => (
            <Notification
              key={notif.id}
              type={notif.type}
              message={notif.message}
              onClose={() => removeNotification(notif.id)}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* ✅ Titre du formulaire */}
      <motion.h1
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="contact-title"
      >
        {t("contactForm.title")}{" "}
        <span className="highlight">{t("footer.copyrightName")}</span>
      </motion.h1>

      {/* ✅ Formulaire */}
      <motion.form
        ref={form}
        onSubmit={sendEmail}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="contact-form"
      >
        <label>
          {t("contactForm.firstName")}
          <input
            type="text"
            name="first_name"
            placeholder={t("contactForm.firstNamePlaceholder")}
            required
          />
        </label>
        <label>
          {t("contactForm.lastName")}
          <input
            type="text"
            name="last_name"
            placeholder={t("contactForm.lastNamePlaceholder")}
            required
          />
        </label>
        <label>
          {t("contactForm.region")}
          <input
            type="text"
            name="region"
            placeholder={t("contactForm.regionPlaceholder")}
            required
          />
        </label>
        <label>
          {t("contactForm.email")}
          <input
            type="email"
            name="user_email"
            placeholder={t("contactForm.emailPlaceholder")}
            required
          />
        </label>
        <label>
          {t("contactForm.message")}
          <textarea
            name="message"
            placeholder={t("contactForm.messagePlaceholder")}
            value={prefilledMessage} // ✅ Utilisation de la valeur pré-remplie
            onChange={(e) => setPrefilledMessage(e.target.value)} // ✅ Permet de modifier le message
            rows={5}
            required
          ></textarea>
        </label>

        {/* ✅ Google reCAPTCHA */}
        <div className="captcha-container">
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}
            onChange={(token) => setCaptchaToken(token)}
            onExpired={() => setCaptchaToken(null)}
          />
        </div>

        {/* ✅ Bouton d'envoi */}
        <div className="submit-button-container">
          <motion.button type="submit" className="submit-button">
            {t("contactForm.submit")} <FaPaperPlane />
          </motion.button>
        </div>
      </motion.form>
    </>
  );
};

export default ContactForm;
